<script setup lang="ts">
  import { defaultMessageDurationSeconds } from '@/utils'
  import { Button, message } from 'ant-design-vue'
  import {
    CopyOutlined,
    DownloadOutlined,
    EditOutlined,
    PlayCircleOutlined,
    SearchOutlined,
    UploadOutlined
  } from '@ant-design/icons-vue'
  import { nextTick, onMounted, onUnmounted, reactive, ref, watchEffect } from 'vue'
  import { useDark } from '@vueuse/core'
  import VueJsonPretty from 'vue-json-pretty'
  import 'vue-json-pretty/lib/styles.css'
  import JSONEditor from 'jsoneditor'
  import { debounceTime, Subject } from 'rxjs'
  import { TableSchema } from '../models/TableSchema'
  import { Table } from '../models/Table'
  import { TablesService } from '../services/TablesService'
  import { TablesJsonParsingService } from '../services/TablesJsonParsingService'

  interface ITableJsonManagementProps {
    tableSchemas: Array<TableSchema>
    tables: Array<Table>
    visibilityChanged: boolean
    parentExecutionId: string
    executionName: string
    tablesJsonParsingService: TablesJsonParsingService
  }

  interface ITableJsonManagementEmits {
    (e: 'tables-applied'): void
    (e: 'update:executionName', newValue: string): void
  }

  const props = defineProps<ITableJsonManagementProps>()
  const emit = defineEmits<ITableJsonManagementEmits>()

  const nonAppliedTables = reactive<Array<Table>>([...props.tables])
  const activeTables = reactive<Array<Table>>(props.tables)
  const nonAppliedExecutionName = ref<string>(props.executionName)

  const activeView = ref<'view' | 'edit'>('view')
  const editorHasChanges = ref<boolean>(false)

  const formattedTablesJson = ref<any>()
  const dependencyChange$ = new Subject<void>()

  dependencyChange$.pipe(debounceTime(300)).subscribe(() => {
    formattedTablesJson.value = TablesService.getFormattedTablesJson(
      props.parentExecutionId,
      nonAppliedExecutionName.value,
      activeTables
    )
  })

  watchEffect(() => {
    props.parentExecutionId,
    nonAppliedExecutionName.value,
    activeTables
    dependencyChange$.next()
  })

  const importedJson = ref<string | undefined>()
  const isDarkMode = useDark()
  const editorContainer = ref<HTMLElement | null>(null)
  let editor: any = null

  const toggleViewMode = () => {
    activeView.value = activeView.value === 'view' ? 'edit' : 'view'
    nextTick(() => {
      if (activeView.value === 'edit') {
        initializeJSONEditor()
      } else {
        destroyEditor()
      }
    })
  }

  const destroyEditor = () => {
    if (editor) {
      editor.destroy()
    }
  }

  const copyJsonToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(
        TablesService.getFormattedTablesJsonString(
          props.parentExecutionId,
          nonAppliedExecutionName.value,
          activeTables
        )
      )
      message.success('JSON copied to clipboard!', defaultMessageDurationSeconds)
    } catch (error) {
      message.error('Failed to copy to clipboard', defaultMessageDurationSeconds)
    }
  }

  const fileInput = ref<HTMLInputElement | null>(null)

  const downloadJsonFile = () => {
    const jsonContent = TablesService.getFormattedTablesJsonString(
      props.parentExecutionId,
      nonAppliedExecutionName.value,
      activeTables
    )
    const blob = new Blob([jsonContent], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `tables-${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    message.success('JSON file downloaded!', defaultMessageDurationSeconds)
  }

  const importJsonFile = () => {
    nonAppliedTables.splice(0, nonAppliedTables.length)
    nonAppliedExecutionName.value = ''
    setJSONEditorModel(props.parentExecutionId, nonAppliedExecutionName.value, nonAppliedTables)
    editorHasChanges.value = true
    fileInput.value?.click()
  }

  const handleFileSelected = (event: Event) => {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    if (file) {
      if (!file.name.endsWith('.json')) {
        message.error('Please select a valid .json file', defaultMessageDurationSeconds)
        return
      }
      const reader = new FileReader()
      reader.onload = () => {
        importedJson.value = reader.result as string
        const result = updateNonAppliedTablesFromJsonString(importedJson.value)
        if (result === true)
          message.success('JSON parsed successfully.', defaultMessageDurationSeconds)
      }
      reader.readAsText(file)
    }
    input.value = ''
  }

  const updateNonAppliedTablesFromJsonString = (json: string): boolean => {
    if (json == null) throw new Error('valid json required')
    const result = props.tablesJsonParsingService.tryParseEntitiesFromJson(json, props.tableSchemas)
    if (!result.valid) {
      message.error(
        `Error parsing JSON: ${result.errors?.join(', ')}`,
        defaultMessageDurationSeconds
      )
      return false
    } else {
      nonAppliedTables.splice(
        0,
        nonAppliedTables.length,
        ...(result.parsedEntities as Array<Table>)
      )
      nonAppliedExecutionName.value = result.name ?? ''
      setJSONEditorModel(props.parentExecutionId, nonAppliedExecutionName.value, nonAppliedTables)
      return true
    }
  }

  const applyJsonTables = () => {
    try {
      const updatedPayloadString = editor?.getText()
      const result = updateNonAppliedTablesFromJsonString(updatedPayloadString)
      if (result === true) {
        message.success('JSON parsed and applied successfully.', defaultMessageDurationSeconds)
        activeTables.splice(0, activeTables.length, ...nonAppliedTables)
        emit('update:executionName', nonAppliedExecutionName.value)
        editorHasChanges.value = false
      }
      emit('tables-applied')
    } catch (e) {
      message.error(`Error getting payload value from JSONEditor`, defaultMessageDurationSeconds)
    }
  }

  const initializeJSONEditor = () => {
    if (editorContainer.value) {
      editorHasChanges.value = false
      editor = new JSONEditor(editorContainer.value, {
        mode: 'code',
        onError: (err: Error) => console.error('Error:', err),
        onChange: () => {
          editorHasChanges.value = true
        }
      })
      setJSONEditorModel(props.parentExecutionId, nonAppliedExecutionName.value, activeTables)
    }
  }

  const setJSONEditorModel = (
    parentExecutionId: string,
    executionName: string,
    tables: Array<Table>
  ) => {
    if (editor != null && editorContainer.value != null) {
      const json = TablesService.getFormattedTablesJson(parentExecutionId, executionName, tables)
      editor.set(json)
      if (editor.getMode() === 'tree') editor.expandAll()
    }
  }

  onMounted(() => {
    initializeJSONEditor()
    formattedTablesJson.value = TablesService.getFormattedTablesJson(
      props.parentExecutionId,
      nonAppliedExecutionName.value,
      activeTables
    )
  })

  onUnmounted(() => {
    destroyEditor()
  })
</script>

<template>
  <div class="flex h-full flex-col">
    <!-- Hidden file input -->
    <input type="file" ref="fileInput" accept=".json" class="hidden" @change="handleFileSelected" />

    <!-- Header Actions -->
    <div class="mb-4 flex justify-end gap-2">
      <a-tooltip placement="bottom" v-if="activeView === 'view'">
        <template #title>{{ 'Edit or Import JSON' }}</template>
        <Button class="flex items-center" type="primary" @click="toggleViewMode">
          <EditOutlined />
          {{ 'Edit JSON' }}
        </Button>
      </a-tooltip>

      <a-tooltip placement="bottom" v-else-if="activeView === 'edit'">
        <template #title>
          {{ editorHasChanges === true ? 'All changes will be lost.' : 'Select to view JSON.' }}
        </template>
        <Button
          :class="['flex items-center', { warning: editorHasChanges === true }]"
          @click="toggleViewMode"
        >
          <SearchOutlined />
          {{ 'View Only' }}
        </Button>
      </a-tooltip>

      <a-tooltip placement="bottom">
        <template #title>Copy Applied JSON to clipboard</template>
        <Button class="flex items-center" @click="copyJsonToClipboard">
          <CopyOutlined />
          Copy to Clipboard
        </Button>
      </a-tooltip>

      <a-tooltip placement="bottom">
        <template #title>Download Applied JSON</template>
        <Button class="flex items-center" @click="downloadJsonFile">
          <DownloadOutlined />
          Download JSON
        </Button>
      </a-tooltip>

      <Button class="flex items-center" :disabled="activeView !== 'edit'" @click="importJsonFile">
        <UploadOutlined />
        Import JSON
      </Button>
      <Button
        type="primary"
        class="flex items-center"
        :disabled="activeView === 'view' || editorHasChanges === false"
        @click="applyJsonTables"
      >
        <PlayCircleOutlined />
        Apply
      </Button>
    </div>

    <!-- JSON Content -->
    <div class="flex-1">
      <div class="relative z-[10] flex"></div>
      <div class="z-[1] mt-[10px]">
        <!-- View -->
        <div
          v-if="activeView === 'view'"
          class="overflow-auto rounded-sm border bg-card p-1"
          style="max-height: calc(100vh - 300px)"
        >
          <div :class="['json-pretty-wrapper', 'viewer-container', { 'dark-mode': isDarkMode }]">
            <vue-json-pretty :data="formattedTablesJson as any" />
          </div>
        </div>
        <!-- Edit -->
        <div class="editor-container" v-else-if="activeView === 'edit'" ref="editorContainer"></div>
      </div>
    </div>
  </div>
</template>

<style>
  .editor-container {
    height: calc(100vh - 165px);
  }
  .viewer-container {
    min-height: calc(100vh - 165px);
    height: 100%;
  }
</style>
